import Router from 'next/router';

import { RECRUIT_PATH_PREFIX } from 'common/constants';

import { ApplicantDocumentFile, DBSCertificateDetails, VisaType } from 'features/documents-upload/constants';

export enum ApplicantView {
  // Returned during the new joiner flow
  NEXT_STEP = 0,
  RESUBMITTED_CHECK_EMAIL = 1,
  SUCCESSFUL = 2,
  NEEDS_REC_REVIEW = 4,
  DOCUMENTS = 6,
  BOOK_CALL = 7,
  RESCHEDULE_CALL = 8,
  REBOOK_CALL = 9,
  DOCUMENTS_UPLOAD = 10,
  REJECTED = 11,
  ALREADY_ACTIVE_WORKER = 12,
  DOCUMENTS_COMPLETE = 13,
  REJECTED_AREA_CLOSED = 14,
  // Returned during the new joiner and rejoiner flows
  REJOINER_EMAIL_SENT_TO_REACTIVATE = 15,
  REJOINER_EMAIL_SENT_TO_BOOK_CALL = 16,
  REJOINER_REJECTED = 17,
  // Returned during the rejoiner flow
  REJOINER_NO_PREVIOUS_WORKER_MATCH_FOUND = 18
}

export enum ApplicantRegion {
  UK = 'uk',
  IRELAND = 'ireland',
  EU = 'eu_eea_or_swiss',
  REST_OF_WORLD = 'rest_of_world'
}

export enum ApplicantState {
  SUCCESSFUL = 'success',
  FORGOTTEN = 'forgot',
  REJECTED = 'rejected',
  CLOSED = 'closed',
  OPEN = 'applied'
}

export interface ApplicantResponse {
  id: string;
  campaign?: string;
  dbsDocument?: ApplicantDocumentFile;
  dbsCertificate?: DBSCertificateDetails;
  claimsToHaveDbsCertificate?: boolean;
  dateOfBirth?: Date;
  email?: string;
  hasEuSettlementCode?: boolean;
  hasRowRightToWorkCode?: boolean;
  claimsToHaveDownloadedApp?: boolean;
  claimsToHaveDownloadedZoom?: boolean;
  euSettlementCode?: string;
  rowRightToWorkCode?: string;
  nationalInsuranceNumber?: string;
  fullName?: string;
  isClosed?: boolean;
  nationality?: string;
  profilePhoto?: ApplicantDocumentFile;
  proofOfId?: ApplicantDocumentFile[];
  state?: ApplicantState;
  region?: ApplicantRegion;
  token?: string;
  view: ApplicantView;
  visa?: VisaType;
  requiresDbsDocumentUpload?: boolean;
}

export function redirectApplicant(applicant: ApplicantResponse, params: any = {}) {
  switch (applicant.view) {
    case ApplicantView.RESUBMITTED_CHECK_EMAIL: {
      Router.push({
        pathname: `${RECRUIT_PATH_PREFIX}/cleaner/form-complete-email/[applicantId]`,
        query: {
          applicantId: applicant.id,
          ...params
        }
      });
      break;
    }
    case ApplicantView.SUCCESSFUL: {
      Router.push({
        pathname: `${RECRUIT_PATH_PREFIX}/cleaner/form-complete-video/[applicantId]/[token]`,
        query: {
          applicantId: applicant.id,
          token: applicant.token,
          ...params
        }
      });
      break;
    }
    case ApplicantView.NEEDS_REC_REVIEW: {
      Router.push({
        pathname: `${RECRUIT_PATH_PREFIX}/cleaner/form-complete-thankyou`,
        query: { ...params }
      });
      break;
    }
    case ApplicantView.DOCUMENTS: {
      Router.push({
        pathname: `${RECRUIT_PATH_PREFIX}/cleaner/documents/[applicantId]/[token]`,
        query: {
          applicantId: applicant.id,
          token: applicant.token,
          ...params
        }
      });
      break;
    }
    case ApplicantView.BOOK_CALL: {
      Router.push({
        pathname: `${RECRUIT_PATH_PREFIX}/cleaner/call-choose-first/[applicantId]/[token]`,
        query: {
          applicantId: applicant.id,
          token: applicant.token,
          ...params
        }
      });
      break;
    }
    case ApplicantView.RESCHEDULE_CALL: {
      Router.push({
        pathname: `${RECRUIT_PATH_PREFIX}/cleaner/call-choose-reschedule/[applicantId]/[token]`,
        query: {
          applicantId: applicant.id,
          token: applicant.token,
          ...params
        }
      });
      break;
    }
    case ApplicantView.REBOOK_CALL: {
      Router.push({
        pathname: `${RECRUIT_PATH_PREFIX}/cleaner/call-choose-rebook/[applicantId]/[token]`,
        query: {
          applicantId: applicant.id,
          token: applicant.token,
          ...params
        }
      });
      break;
    }
    case ApplicantView.DOCUMENTS_UPLOAD: {
      Router.push({
        pathname: `${RECRUIT_PATH_PREFIX}/cleaner/documents-upload/[applicantId]/[token]`,
        query: {
          applicantId: applicant.id,
          token: applicant.token,
          ...params
        }
      });
      break;
    }
    case ApplicantView.REJECTED: {
      Router.push({
        pathname: `${RECRUIT_PATH_PREFIX}/cleaner/form-complete-unable-to-progress`,
        query: { ...params }
      });
      break;
    }
    case ApplicantView.REJECTED_AREA_CLOSED: {
      Router.push({
        pathname: `${RECRUIT_PATH_PREFIX}/cleaner/form-complete-unable-to-progress-area`,
        query: { ...params }
      });
      break;
    }
    case ApplicantView.ALREADY_ACTIVE_WORKER: {
      Router.push({
        pathname: `${RECRUIT_PATH_PREFIX}/cleaner/form-complete-already-housekeeper`,
        query: { ...params }
      });
      break;
    }
    case ApplicantView.DOCUMENTS_COMPLETE: {
      Router.push({
        pathname: `${RECRUIT_PATH_PREFIX}/cleaner/documents-complete/[applicantId]/[token]`,
        query: {
          applicantId: applicant.id,
          token: applicant.token,
          ...params
        }
      });
      break;
    }
    case ApplicantView.REJOINER_NO_PREVIOUS_WORKER_MATCH_FOUND: {
      Router.push(`${RECRUIT_PATH_PREFIX}/rejoin/no-previous-worker`);
      break;
    }
    case ApplicantView.REJOINER_EMAIL_SENT_TO_REACTIVATE: {
      Router.push(`${RECRUIT_PATH_PREFIX}/rejoin/reactivate-email-sent`);
      break;
    }
    case ApplicantView.REJOINER_EMAIL_SENT_TO_BOOK_CALL: {
      Router.push(`${RECRUIT_PATH_PREFIX}/rejoin/book-call-email-sent`);
      break;
    }
    case ApplicantView.REJOINER_REJECTED: {
      Router.push(`${RECRUIT_PATH_PREFIX}/rejoin/unable-to-progress`);
      break;
    }
    default:
      // Fallback error handling
      console.error('Unexpected response view:', applicant.view);
      break;
  }
}

export const filterApplicantViewFromResponse = (response: ApplicantResponse | ApplicantDocumentFile): void => {
  if (
    ![ApplicantView.DOCUMENTS_COMPLETE, ApplicantView.DOCUMENTS_UPLOAD, ApplicantView.REJECTED].includes(response.view)
  ) {
    response.view = ApplicantView.DOCUMENTS_UPLOAD;
  }
};
